import "./home.css";

interface Bank {
    id: string;
    name: string;
    bic: string;
    transaction_total_days: string;
    countries: string[];
    logo?: string;
}

const WORKER_URL = "https://banking.jcmpagel.workers.dev";
const LANGUAGE_STORAGE_KEY = "bdm.language";
// Replace with your live Stripe Payment Link or Checkout URL.
const STRIPE_PRO_CHECKOUT_URL = "https://buy.stripe.com/00weVegxu4tGaz7axL9fW01";
let allBanks: Bank[] = [];
let currentCountry = "DE";
let currentLanguage: "en" | "de" = "en";

const TEXT_DE: Record<string, string> = {
    // Shared navigation/footer
    "Bank Connector": "Bank Connector",
    "Solutions": "Lösungen",
    "Network": "Netzwerk",
    "Security": "Sicherheit",
    "Pricing": "Preise",
    "Launch App": "App starten",

    "Terms of Service": "Nutzungsbedingungen",
    "Support": "Support",
    "All rights reserved.": "Alle Rechte vorbehalten.",
    "© 2026. All rights reserved.": "© 2026. Alle Rechte vorbehalten.",

    // Home page
    "From Rent Collection to Expense Tracking.": "Von Mieteingang bis Ausgabenkontrolle.",
    "Powered by": "Gestützt auf",
    "Live Data": "Live-Daten",
    "Connect 2,300+ financial institutions across 30 supported countries directly to Excel. Built for landlords, companies, and anyone who wants to track rent, expenses, and cash flow with live banking data.": "Verbinden Sie 2.300+ Finanzinstitute in 30 unterstützten Ländern direkt mit Excel. Entwickelt für Vermieter, Unternehmen und alle, die Mieten, Ausgaben und Cashflow mit Live-Bankdaten verfolgen möchten.",
    "Built for landlords, companies, finance teams, and anyone who wants clear bank activity in Excel. Track rent payments, monitor expenses, and stop manual CSV imports.": "Entwickelt für Vermieter, Unternehmen, Finanzteams und alle, die klare Bankaktivitäten in Excel sehen möchten. Verfolgen Sie Mieteingänge, überwachen Sie Ausgaben und beenden Sie manuelle CSV-Importe.",
    "Download on Microsoft Store": "Im Microsoft Store herunterladen",
    "Also available in Microsoft AppSource for Excel.": "Auch in Microsoft AppSource für Excel verfügbar.",
    "Start Free Trial": "Kostenlos testen",
    "How it works": "So funktioniert es",
    "Bank-grade security": "Sicherheit auf Bankniveau",
    "No credit card required": "Keine Kreditkarte erforderlich",
    "Booking Date": "Buchungsdatum",
    "Date": "Datum",
    "Merchant": "Händler",
    "Description": "Beschreibung",
    "Amount": "Betrag",

    "Type": "Typ",
    "Category": "Kategorie",
    "IBAN": "IBAN",
    "Debtor": "Debitor",
    "Creditor": "Kreditor",
    "Infrastructure": "Infrastruktur",
    "Income": "Einnahmen",

    "Synced": "Synchronisiert",
    "Live Sync...": "Live-Synchronisierung...",
    "CONNECTING TO OVER 2,300 INSTITUTIONS ACROSS 30 COUNTRIES": "VERBINDUNG ZU ÜBER 2.300 INSTITUTEN IN 30 LÄNDERN",
    "How to Connect Your Bank Account": "So verbinden Sie Ihr Bankkonto",
    "A clear 5-step flow inside Excel, based on the same setup used in the add-in.": "Ein klarer 5-Schritte-Ablauf in Excel, basierend auf derselben Einrichtung wie im Add-in.",
    "Open the Add-in in Excel": "Add-in in Excel öffnen",
    "Go to Bank Connector and choose your country (for example Germany or Austria).": "Öffnen Sie Bank Connector und wählen Sie Ihr Land (z. B. Deutschland oder Österreich).",
    "Select Your Institution": "Ihr Institut auswählen",
    "Search your bank by name or BIC, then pick the correct provider from the list.": "Suchen Sie Ihre Bank nach Name oder BIC und wählen Sie dann den passenden Anbieter aus der Liste.",
    "Configure Connection": "Verbindung konfigurieren",
    "Set authorization period and history range (for example 90 days access and up to 730 days history).": "Legen Sie Autorisierungszeitraum und Historie fest (z. B. 90 Tage Zugriff und bis zu 730 Tage Verlauf).",
    "Authorize Securely": "Sicher autorisieren",
    "Open the generated bank link, complete login in your bank app, and confirm consent.": "Öffnen Sie den erzeugten Banklink, schließen Sie den Login in Ihrer Banking-App ab und bestätigen Sie die Zustimmung.",
    "Sync and Categorize": "Synchronisieren und kategorisieren",
    "Your data is imported to Transactions, then you can run Mapping rules and refresh anytime.": "Ihre Daten werden in Transaktionen importiert, danach können Sie Mapping-Regeln anwenden und jederzeit aktualisieren.",
    "Connection Setup Example": "Beispiel für Verbindungseinrichtung",
    "PSD2 Secure": "PSD2-sicher",
    "Step 1: Select Region": "Schritt 1: Region auswählen",
    "Connect": "Verbinden",
    "Pick your country first so we load supported institutions and history limits.": "Wählen Sie zuerst Ihr Land, damit unterstützte Institute und Historienlimits geladen werden.",
    "Other Markets...": "Weitere Märkte...",
    "Step 2: Choose Institution": "Schritt 2: Institut auswählen",
    "Search": "Suche",
    "Search by name or BIC...": "Nach Name oder BIC suchen...",
    "Select the exact bank/provider you want to connect.": "Wählen Sie genau die Bank bzw. den Anbieter, den Sie verbinden möchten.",
    "Step 3: Configure Connection": "Schritt 3: Verbindung konfigurieren",
    "Settings": "Einstellungen",
    "Maximum available": "Maximal verfügbar",
    "730 days": "730 Tage",
    "Action": "Aktion",
    "Securely Connect": "Sicher verbinden",
    "Set access duration and how much historical data should be imported.": "Legen Sie die Zugriffsdauer und den zu importierenden Verlauf fest.",
    "Step 4: Authorize in Browser": "Schritt 4: Im Browser autorisieren",
    "Created": "Erstellt",
    "Authenticating": "Authentifizierung",
    "Open the generated link, log in at your bank, and approve consent.": "Öffnen Sie den erzeugten Link, melden Sie sich bei Ihrer Bank an und bestätigen Sie die Zustimmung.",
    "Step 5: Sync Output Sheets": "Schritt 5: Ausgabeblätter synchronisieren",
    "Ready": "Bereit",
    "Just now": "Gerade eben",
    "Sync New Data": "Neue Daten synchronisieren",
    "Data lands in Excel and can be refreshed and categorized at any time.": "Die Daten landen in Excel und können jederzeit aktualisiert und kategorisiert werden.",
    "Country": "Land",
    "Institution": "Institut",
    "Authorization Period": "Autorisierungszeitraum",
    "90 days (Recommended)": "90 Tage (Empfohlen)",
    "Sync History": "Synchronisierungsverlauf",
    "Last 2 years (730 days)": "Letzte 2 Jahre (730 Tage)",
    "Status": "Status",
    "Linked": "Verbunden",
    "Example output sheets:": "Beispiel-Ausgabeblätter:",
    "Why Excel works for every money workflow.": "Warum Excel für jeden Geld-Workflow funktioniert.",
    "From rent tracking to company spend control, Excel is flexible enough for your process. We bring the live data, you keep full control.": "Von Mietverfolgung bis Ausgabenkontrolle im Unternehmen: Excel ist flexibel genug für Ihren Prozess. Wir liefern Live-Daten, Sie behalten die volle Kontrolle.",
    "Native Pivot Tables": "Native Pivot-Tabellen",
    "Analyze millions of transaction rows using the tools you've mastered. No new learning curve.": "Analysieren Sie Millionen Transaktionszeilen mit den Tools, die Sie bereits beherrschen. Keine neue Lernkurve.",
    "Complex Formulas": "Komplexe Formeln",
    "Use live bank balances in your XLOOKUPs, IF statements, and cash flow projections instantly.": "Verwenden Sie Live-Kontostände sofort in XLOOKUPs, WENN-Formeln und Cashflow-Prognosen.",
    "Total Ownership": "Volle Datenhoheit",
    "The data lives in your workbook, not in our cloud. You own your financial history.": "Die Daten bleiben in Ihrer Arbeitsmappe, nicht in unserer Cloud. Ihre Finanzhistorie gehört Ihnen.",
    "Cash Flow Forecast [Live]": "Cashflow-Prognose [Live]",
    "Rows: Category": "Zeilen: Kategorie",
    "Columns: Month": "Spalten: Monat",
    "Values: Sum of Amount": "Werte: Summe Betrag",
    "Jan": "Jan",
    "Feb": "Feb",
    "Mar": "Mär",
    "Grand Total": "Gesamtsumme",
    "Rent": "Miete",
    "Subscriptions": "Abonnements",
    "Travel": "Reisen",
    "Built for Complex Portfolios": "Entwickelt für komplexe Portfolios",
    "Whether you manage real estate, SME finances, or personal wealth.": "Ob Immobilien, KMU-Finanzen oder privates Vermögen: Es passt zu Ihrem Einsatz.",
    "Real Estate Management": "Immobilienverwaltung",
    "Track incoming rent across multiple properties and bank accounts in one consolidated view. Auto-flag late payments.": "Verfolgen Sie Mieteingänge über mehrere Objekte und Konten in einer konsolidierten Ansicht. Verspätete Zahlungen werden automatisch markiert.",
    "Expense Consolidation": "Ausgabenkonsolidierung",
    "Merge transaction feeds from all employee cards. Automatically map vendors (e.g., \"Uber\") to your GL accounting codes.": "Führen Sie Transaktionsfeeds aller Mitarbeiterkarten zusammen. Anbieter (z. B. \"Uber\") werden automatisch Ihren Buchungscodes zugeordnet.",
    "Liquidity Planning": "Liquiditätsplanung",
    "Stop waiting for monthly statements. See your exact cash position across 14+ currencies in real-time.": "Warten Sie nicht mehr auf Monatsauszüge. Sehen Sie Ihre exakte Liquiditätsposition über 14+ Währungen in Echtzeit.",
    "Rules-Based Mapping": "Regelbasiertes Mapping",
    "Define merchant patterns once and auto-assign category and subcategory on every sync.": "Definieren Sie Händler-Muster einmal und weisen Sie Kategorie und Unterkategorie bei jeder Synchronisierung automatisch zu.",
    "Identifier Matching": "Identifier-Abgleich",
    "Use vendor names, IBAN fragments, or text patterns to classify entries consistently.": "Nutzen Sie Anbieternamen, IBAN-Fragmente oder Textmuster, um Buchungen konsistent zu klassifizieren.",
    "Category + Subcategory": "Kategorie + Unterkategorie",
    "Map every transaction to reporting-friendly fields right when data lands in Excel.": "Ordnen Sie jede Transaktion sofort beim Eintreffen in Excel reportingtauglichen Feldern zu.",
    "Reusable Rules": "Wiederverwendbare Regeln",
    "Once configured, mappings are reused on every refresh with no manual cleanup.": "Einmal eingerichtet, werden Zuordnungen bei jeder Aktualisierung ohne manuelle Nacharbeit wiederverwendet.",
    "Creditor/Debtor Type": "Kreditor-/Debitor-Typ",
    "Identifier": "Identifier",
    "Sub Category": "Unterkategorie",
    "Shopping": "Einkauf",
    "Online": "Online",
    "Entertainment": "Unterhaltung",
    "Subscription": "Abonnement",
    "Food & Drink": "Essen & Trinken",
    "Cafe": "Café",
    "Monthly": "Monatlich",
    "Mapped automatically on import": "Beim Import automatisch zugeordnet",
    "Control and Visibility": "Kontrolle und Transparenz",
    "Close your books faster with automated checks and real-time account overviews.": "Schließen Sie Ihre Bücher schneller mit automatisierten Prüfungen und Echtzeit-Übersichten über Konten.",
    "Reconciliation Exceptions": "Abstimmungsabweichungen",
    "2 open items": "2 offene Posten",
    "Expected": "Erwartet",
    "Actual": "Ist",
    "Difference": "Differenz",
    "Open": "Offen",
    "Matched": "Abgeglichen",
    "Cash Position by Account": "Liquiditätsposition nach Konto",
    "Updated 2m ago": "Vor 2 Min. aktualisiert",
    "Bank": "Bank",
    "Account": "Konto",
    "Currency": "Währung",
    "Available": "Verfügbar",
    "Booked": "Gebucht",
    "Last Sync": "Letzte Sync",
    "Total (EUR Eq.)": "Gesamt (EUR-Äquiv.)",
    "Live": "Live",
    "Explore Our Network": "Unser Netzwerk entdecken",
    "Direct connectivity to 2,300+ institutions across 30 supported countries. Search for your bank below.": "Direkte Anbindung an 2.300+ Institute in 30 unterstützten Ländern. Suchen Sie Ihre Bank unten.",
    "🇩🇪 Germany": "🇩🇪 Deutschland",
    "🇦🇹 Austria": "🇦🇹 Österreich",
    "Germany": "Deutschland",
    "Austria": "Österreich",
    "Other Markets": "Weitere Märkte",
    "Select a country...": "Land auswählen...",
    "🇧🇪 Belgium": "🇧🇪 Belgien",
    "🇧🇬 Bulgaria": "🇧🇬 Bulgarien",
    "🇨🇾 Cyprus": "🇨🇾 Zypern",
    "🇨🇿 Czech Republic": "🇨🇿 Tschechien",
    "🇩🇰 Denmark": "🇩🇰 Dänemark",
    "🇪🇪 Estonia": "🇪🇪 Estland",
    "🇪🇸 Spain": "🇪🇸 Spanien",
    "🇫🇮 Finland": "🇫🇮 Finnland",
    "🇫🇷 France": "🇫🇷 Frankreich",
    "🇬🇷 Greece": "🇬🇷 Griechenland",
    "🇭🇷 Croatia": "🇭🇷 Kroatien",
    "🇭🇺 Hungary": "🇭🇺 Ungarn",
    "🇮🇪 Ireland": "🇮🇪 Irland",
    "🇮🇸 Iceland": "🇮🇸 Island",
    "🇮🇹 Italy": "🇮🇹 Italien",
    "🇱🇮 Liechtenstein": "🇱🇮 Liechtenstein",
    "🇱🇹 Lithuania": "🇱🇹 Litauen",
    "🇱🇺 Luxembourg": "🇱🇺 Luxemburg",
    "🇱🇻 Latvia": "🇱🇻 Lettland",
    "🇲🇹 Malta": "🇲🇹 Malta",
    "🇳🇱 Netherlands": "🇳🇱 Niederlande",
    "🇳🇴 Norway": "🇳🇴 Norwegen",
    "🇵🇱 Poland": "🇵🇱 Polen",
    "🇵🇹 Portugal": "🇵🇹 Portugal",
    "🇷🇴 Romania": "🇷🇴 Rumänien",
    "🇸🇪 Sweden": "🇸🇪 Schweden",
    "🇸🇮 Slovenia": "🇸🇮 Slowenien",
    "🇸🇰 Slovakia": "🇸🇰 Slowakei",
    "Search by bank name or BIC...": "Nach Bankname oder BIC suchen...",
    "Loading institutional data...": "Institutsdaten werden geladen...",
    "Don't see your bank? We add new institutions weekly.": "Ihre Bank fehlt? Wir fügen wöchentlich neue Institute hinzu.",
    "Request integration": "Integration anfragen",
    "Simple, Transparent Pricing": "Einfaches, transparentes Preismodell",
    "No hidden fees. Cancel anytime.": "Keine versteckten Gebühren. Jederzeit kündbar.",
    "Sandbox": "Sandbox",
    "For testing and validation": "Für Tests und Validierung",
    "Simulated Bank Data": "Simulierte Bankdaten",
    "Full Excel Integration": "Volle Excel-Integration",
    "Unlimited Testing": "Unbegrenzte Tests",
    "Try Demo Mode": "Demomodus testen",
    "ENTERPRISE STANDARD": "ENTERPRISE-STANDARD",
    "Professional": "Professional",
    "/mo": "/Monat",
    "For live operational finance": "Für den operativen Live-Einsatz",
    "Up to 5": "Bis zu 5",
    "Bank Account Connections": "Bankkontoverbindungen",
    "Unlimited": "Unbegrenzte",
    "Live Connections": "Live-Verbindungen",
    "Institutions (30 Countries)": "Institute (30 Länder)",
    "Up to 730 Days": "Bis zu 730 Tage",
    "Transaction History": "Transaktionsverlauf",
    "2 Years": "2 Jahre",
    "History Access": "Verlaufszugriff",
    "Multi-Currency Support": "Mehrwährungsunterstützung",
    "Custom Mapping Rules": "Individuelle Zuordnungsregeln",
    "Priority Email Support": "Priorisierter E-Mail-Support",
    "Get License": "Lizenz erhalten",
    "Activation and license key delivery can take up to 24 hours after purchase.": "Aktivierung und Zustellung des Lizenzschlüssels können nach dem Kauf bis zu 24 Stunden dauern.",
    "Security First:": "Sicherheit zuerst:",
    "We operate under strict PSD2 regulations. Your banking credentials are never seen or stored by our servers. Connections are encrypted via Nordigen/GoCardless.": "Wir arbeiten unter strengen PSD2-Vorgaben. Ihre Banking-Zugangsdaten werden von unseren Servern weder gesehen noch gespeichert. Verbindungen sind über Nordigen/GoCardless verschlüsselt.",

    // Privacy page
    "Privacy Policy": "Datenschutzrichtlinie",
    "We believe in total transparency. Here is exactly how your data moves.": "Wir glauben an volle Transparenz. So bewegen sich Ihre Daten.",
    "Last Updated: February 14, 2026": "Zuletzt aktualisiert: 14. Februar 2026",
    "1. Architecture Overview": "1. Architekturüberblick",
    "Bank Connector operates as a pass-through tool between your Excel workbook and your bank. We prioritize client-side processing to minimize data exposure.": "Bank Connector arbeitet als Durchleitungs-Tool zwischen Ihrer Excel-Arbeitsmappe und Ihrer Bank. Wir priorisieren clientseitige Verarbeitung, um Datenexposition zu minimieren.",
    "Bank Connector operates as a": "Bank Connector arbeitet als",
    "pass-through tool": "Durchleitungs-Tool",
    "between your Excel workbook and your bank. We prioritize client-side processing to minimize data exposure.": "zwischen Ihrer Excel-Arbeitsmappe und Ihrer Bank. Wir priorisieren clientseitige Verarbeitung, um Datenexposition zu minimieren.",
    "Data Flow:": "Datenfluss:",
    "Excel (Your Device) → Cloudflare Worker (Our Proxy) → GoCardless (Provider) → Your Bank": "Excel (Ihr Gerät) → Cloudflare Worker (unser Proxy) → GoCardless (Anbieter) → Ihre Bank",
    "Excel (Your Device) &nbsp;&#8594;&nbsp; Cloudflare Worker (Our Proxy) &nbsp;&#8594;&nbsp; GoCardless (Provider) &nbsp;&#8594;&nbsp; Your Bank": "Excel (Ihr Gerät) → Cloudflare Worker (unser Proxy) → GoCardless (Anbieter) → Ihre Bank",
    "2. Data We Store": "2. Daten, die wir speichern",
    "To provide our service and enforce licensing, we persist minimal metadata. This data is stored in a secure Cloudflare D1 database.": "Um unseren Service bereitzustellen und Lizenzen durchzusetzen, speichern wir minimale Metadaten. Diese Daten liegen in einer sicheren Cloudflare-D1-Datenbank.",
    "License Keys": "Lizenzschlüssel",
    "We store your unique license key to validate your subscription status.": "Wir speichern Ihren eindeutigen Lizenzschlüssel, um Ihren Abonnementstatus zu prüfen.",
    "Connected Bank Names": "Namen verbundener Banken",
    "We record the name of connected banks (e.g., \"Deutsche Bank\") solely to enforce connection limits (e.g., max 5 banks).": "Wir erfassen den Namen verbundener Banken (z. B. \"Deutsche Bank\") ausschließlich zur Durchsetzung von Verbindungslimits (z. B. max. 5 Banken).",
    "We record the": "Wir erfassen den",
    "name": "Namen",
    "of connected banks (e.g., \"Deutsche Bank\") solely to enforce connection limits (e.g., max 5 banks).": "verbundener Banken (z. B. \"Deutsche Bank\") ausschließlich zur Durchsetzung von Verbindungslimits (z. B. max. 5 Banken).",
    "3. Data We Process (But Do NOT Store)": "3. Daten, die wir verarbeiten (aber NICHT speichern)",
    "The following data passes through our secure proxy to reach your Excel sheet but is never saved, logged, or retained on our servers:": "Die folgenden Daten laufen über unseren sicheren Proxy zu Ihrem Excel-Blatt, werden aber auf unseren Servern niemals gespeichert, protokolliert oder aufbewahrt:",
    "The following data passes through our secure proxy to reach your Excel sheet but is": "Die folgenden Daten laufen über unseren sicheren Proxy zu Ihrem Excel-Blatt, werden aber",
    "never saved, logged, or retained": "niemals gespeichert, protokolliert oder aufbewahrt",
    "on our servers:": "auf unseren Servern:",
    "Transaction History:": "Transaktionshistorie:",
    "Dates, amounts, merchant names, and descriptions flow directly to your device.": "Daten, Beträge, Händlernamen und Beschreibungen fließen direkt auf Ihr Gerät.",
    "Account Balances:": "Kontostände:",
    "Real-time balance data is transient.": "Echtzeit-Salden sind nur vorübergehend verfügbar.",
    "Requisition IDs:": "Requisitions-IDs:",
    "The technical identifiers for bank connections are used instantly and discarded.": "Die technischen Kennungen für Bankverbindungen werden direkt verwendet und verworfen.",
    "4. Data We NEVER See": "4. Daten, die wir NIEMALS sehen",
    "We strictly adhere to PSD2 (Open Banking) security standards.": "Wir halten uns strikt an die PSD2-Sicherheitsstandards (Open Banking).",
    "Bank Credentials": "Bankzugangsdaten",
    "We never ask for, see, or store your bank username, password, or PIN. You enter these directly on your bank's secure portal during the initial connection setup (facilitated by GoCardless).": "Wir fragen niemals nach Ihrem Bank-Benutzernamen, Passwort oder Ihrer PIN, sehen diese nicht und speichern sie nicht. Diese geben Sie direkt im sicheren Portal Ihrer Bank bei der Ersteinrichtung ein (bereitgestellt über GoCardless).",
    "5. Third-Party Processors": "5. Drittanbieter-Verarbeiter",
    "We use trusted infrastructure partners to deliver this service:": "Wir nutzen vertrauenswürdige Infrastrukturpartner für diesen Service:",
    "GoCardless (formerly Nordigen):": "GoCardless (ehemals Nordigen):",
    "Provides the authorized Open Banking API to connect with 2,300+ institutions.": "Stellt die autorisierte Open-Banking-API für die Verbindung mit 2.300+ Instituten bereit.",
    "Cloudflare:": "Cloudflare:",
    "Hosts our secure Worker functions and database (D1).": "Hostet unsere sicheren Worker-Funktionen und die Datenbank (D1).",
    "6. Contact Us": "6. Kontakt",
    "For privacy concerns or deletion requests, contact": "Bei Datenschutzfragen oder Löschanfragen kontaktieren Sie",
};

function normalizeText(input: string): string {
    return input.replace(/\s+/g, " ").trim();
}

function getLanguage(): "en" | "de" {
    const stored = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (stored === "en" || stored === "de") {
        return stored;
    }
    return navigator.language.toLowerCase().startsWith("de") ? "de" : "en";
}

function translateText(input: string): string {
    if (currentLanguage !== "de") return input;
    const key = normalizeText(input);
    return TEXT_DE[key] || input;
}

function applyTranslatedTextNode(textNode: Text): void {
    const raw = textNode.nodeValue || "";
    if (!normalizeText(raw)) return;

    const translated = translateText(raw);
    if (translated === raw) return;

    const leading = raw.match(/^\s*/)?.[0] || "";
    const trailing = raw.match(/\s*$/)?.[0] || "";
    textNode.nodeValue = `${leading}${translated}${trailing}`;
}

function applyLanguageToDocument(): void {
    document.documentElement.lang = currentLanguage;

    const languageSelect = document.getElementById("languageSelect") as HTMLSelectElement | null;
    if (languageSelect) {
        languageSelect.value = currentLanguage;
    }

    if (currentLanguage !== "de") return;

    const titleMap: Record<string, string> = {
        "Bank Connector | Enterprise Excel Connectivity": "Bank Connector | Enterprise-Excel-Konnektivität",
        "Privacy Policy | Bank Connector": "Datenschutzrichtlinie | Bank Connector",
    };

    const currentTitle = document.title;
    if (titleMap[currentTitle]) {
        document.title = titleMap[currentTitle];
    }

    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
        const content = metaDescription.getAttribute("content") || "";
        const translated = translateText(content);
        if (translated !== content) {
            metaDescription.setAttribute("content", translated);
        }
    }

    const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
    let node = walker.nextNode();
    while (node) {
        applyTranslatedTextNode(node as Text);
        node = walker.nextNode();
    }

    document.querySelectorAll("input, textarea").forEach((el) => {
        const input = el as HTMLInputElement;
        if (input.placeholder) {
            input.placeholder = translateText(input.placeholder);
        }
    });

    document.querySelectorAll("[title]").forEach((el) => {
        const title = el.getAttribute("title");
        if (!title) return;
        const translated = translateText(title);
        if (translated !== title) {
            el.setAttribute("title", translated);
        }
    });
}

function setLanguage(lang: "en" | "de"): void {
    currentLanguage = lang;
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lang);
    if (lang === "en") {
        window.location.reload();
        return;
    }
    applyLanguageToDocument();
}

function initLanguageSwitcher(): void {
    const select = document.getElementById("languageSelect") as HTMLSelectElement | null;
    if (!select) return;

    currentLanguage = getLanguage();
    select.value = currentLanguage;

    select.addEventListener("change", (e) => {
        const next = (e.target as HTMLSelectElement).value;
        if (next === "en" || next === "de") {
            setLanguage(next);
        }
    });
}

function isConfiguredStripeLink(url: string): boolean {
    if (!url) return false;
    return !url.includes("REPLACE_WITH_YOUR") &&
        (url.startsWith("https://buy.stripe.com/") || url.startsWith("https://checkout.stripe.com/"));
}

function isCommerceRestrictedPlatform(): boolean {
    const ua = navigator.userAgent.toLowerCase();
    const isMobile = /android|iphone|ipad|ipod/.test(ua) ||
        (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

    let officeCommerceBlocked = false;
    try {
        // Available when page runs inside Office host context.
        // @ts-ignore Office global exists in add-in surfaces.
        officeCommerceBlocked = typeof Office !== "undefined" && Office.context?.commerceAllowed === false;
    } catch {
        officeCommerceBlocked = false;
    }

    return isMobile || officeCommerceBlocked;
}

function configureStripeCheckoutLinks(): void {
    if (isCommerceRestrictedPlatform()) {
        document.querySelectorAll(".commerce-only").forEach((el) => {
            (el as HTMLElement).style.display = "none";
        });
        return;
    }

    const checkoutCtas = ["proStripeCheckoutBtn", "supportStripeCheckoutBtn"];
    checkoutCtas.forEach((id) => {
        const cta = document.getElementById(id) as HTMLAnchorElement | null;
        if (!cta) return;

        if (isConfiguredStripeLink(STRIPE_PRO_CHECKOUT_URL)) {
            cta.href = STRIPE_PRO_CHECKOUT_URL;
            cta.target = "_blank";
            cta.rel = "noopener noreferrer";
        } else {
            // Fallback so the CTA never points to an invalid checkout URL.
            cta.href = "/support.html";
            cta.removeAttribute("target");
            cta.removeAttribute("rel");
        }
    });
}

function initHowToSteps(): void {
    const stepButtons = Array.from(document.querySelectorAll<HTMLElement>("[data-howto-step]"));
    const stepPanels = Array.from(document.querySelectorAll<HTMLElement>("[data-howto-panel]"));
    if (!stepButtons.length || !stepPanels.length) return;

    const activateStep = (stepId: string) => {
        stepButtons.forEach((btn) => {
            btn.classList.toggle("active", btn.dataset.howtoStep === stepId);
        });
        stepPanels.forEach((panel) => {
            panel.classList.toggle("active", panel.dataset.howtoPanel === stepId);
        });
    };

    stepButtons.forEach((btn) => {
        btn.addEventListener("click", () => {
            const stepId = btn.dataset.howtoStep;
            if (!stepId) return;
            activateStep(stepId);
        });
    });
}

// Initialize landing/privacy page logic
document.addEventListener("DOMContentLoaded", () => {
    console.log("Bank Connector Landing Page Loaded");

    configureStripeCheckoutLinks();
    initLanguageSwitcher();
    applyLanguageToDocument();
    initHowToSteps();

    // Smooth scroll for nav links
    document.querySelectorAll('a[href^="#"]').forEach(anchor => {
        anchor.addEventListener('click', function (this: Element, e) {
            e.preventDefault();
            const href = this.getAttribute('href');
            if (!href) return;
            const target = document.querySelector(href);
            if (target) {
                target.scrollIntoView({
                    behavior: 'smooth'
                });
            }
        });
    });

    // Detect location and initialize only for home page explorer
    if (document.getElementById("banksGrid")) {
        detectLocationAndInit();
    }
});

async function detectLocationAndInit() {
    try {
        console.log("Detecting user location...");
        const response = await fetch('https://ipapi.co/json/');
        const data = await response.json();
        const detectedCountry = data.country_code;

        console.log(`User detected in: ${detectedCountry}`);

        const otherCountries = ["BE", "BG", "CY", "CZ", "DK", "EE", "ES", "FI", "FR", "GR", "HR", "HU", "IE", "IS", "IT", "LI", "LT", "LU", "LV", "MT", "NL", "NO", "PL", "PT", "RO", "SE", "SI", "SK"];

        if (detectedCountry === "AT") {
            currentCountry = "AT";
        } else if (detectedCountry === "DE") {
            currentCountry = "DE";
        } else if (otherCountries.includes(detectedCountry)) {
            currentCountry = detectedCountry;
        } else {
            currentCountry = "DE";
        }

        updateActiveUI(currentCountry);
    } catch (error) {
        console.warn("Geolocation failed, defaulting to DE:", error);
        currentCountry = "DE";
    } finally {
        initBankExplorer();
    }
}

function updateActiveUI(country: string) {
    const tabBtns = document.querySelectorAll(".tab-btn");
    const countryDropdown = document.getElementById("countryDropdown") as HTMLSelectElement;

    tabBtns.forEach(btn => {
        if (btn.getAttribute("data-country") === country) {
            btn.classList.add("active");
        } else {
            btn.classList.remove("active");
        }
    });

    if (countryDropdown && country !== "DE" && country !== "AT") {
        countryDropdown.value = country;
    } else if (countryDropdown) {
        countryDropdown.selectedIndex = 0;
    }
}

async function initBankExplorer() {
    const banksGrid = document.getElementById("banksGrid");
    const tabBtns = document.querySelectorAll(".tab-btn");
    const countryDropdown = document.getElementById("countryDropdown") as HTMLSelectElement;
    const searchInput = document.getElementById("bankSearch") as HTMLInputElement;

    if (!banksGrid || !tabBtns || !searchInput) return;

    loadBanksByCountry(currentCountry);

    tabBtns.forEach(btn => {
        btn.addEventListener("click", () => {
            const country = btn.getAttribute("data-country");
            if (!country || country === currentCountry) return;

            currentCountry = country;
            updateActiveUI(country);
            loadBanksByCountry(country);

            searchInput.value = "";
        });
    });

    if (countryDropdown) {
        countryDropdown.addEventListener("change", () => {
            const country = countryDropdown.value;
            if (!country || country === currentCountry) return;

            currentCountry = country;
            updateActiveUI(country);
            loadBanksByCountry(country);

            searchInput.value = "";
        });
    }

    searchInput.addEventListener("input", (e) => {
        const searchTerm = (e.target as HTMLInputElement).value.toLowerCase();
        filterAndRenderBanks(searchTerm);
    });
}

async function loadBanksByCountry(country: string) {
    const banksGrid = document.getElementById("banksGrid");
    const banksLoader = document.getElementById("banksLoader");

    if (!banksGrid || !banksLoader) return;

    try {
        banksLoader.classList.remove("hidden");
        banksGrid.innerHTML = "";

        const response = await fetch(`./assets/banks_${country.toLowerCase()}.json`);
        if (!response.ok) throw new Error(`Failed to fetch local data for ${country}`);

        allBanks = await response.json();

        banksLoader.classList.add("hidden");
        filterAndRenderBanks("");
    } catch (error) {
        console.error("Error loading banks from cache:", error);
        try {
            const response = await fetch(`${WORKER_URL}/institutions?country=${country}`);
            allBanks = await response.json();
            banksLoader.classList.add("hidden");
            filterAndRenderBanks("");
        } catch {
            banksGrid.innerHTML = `<p class="error">${currentLanguage === "de" ? "Bankdaten konnten nicht geladen werden. Bitte überprüfen Sie Ihre Verbindung." : "Failed to load bank data. Please check your connection."}</p>`;
            banksLoader.classList.add("hidden");
        }
    }
}

function filterAndRenderBanks(searchTerm: string) {
    const banksGrid = document.getElementById("banksGrid");
    if (!banksGrid) return;

    const filtered = allBanks.filter(bank =>
        bank.name.toLowerCase().includes(searchTerm) ||
        bank.bic.toLowerCase().includes(searchTerm)
    );

    if (filtered.length === 0) {
        const msg = currentLanguage === "de"
            ? `Keine Banken gefunden, die zu \"${searchTerm}\" passen.`
            : `No banks found matching \"${searchTerm}\"`;
        banksGrid.innerHTML = `<div class="no-results" style="grid-column: 1/-1; text-align: center; padding: 4rem;">
            <p style="color: var(--color-text-light);">${msg}</p>
        </div>`;
        return;
    }

    const displayLimit = 18;
    const itemsToDisplay = filtered.slice(0, displayLimit);
    const hasMore = filtered.length > displayLimit;

    let html = itemsToDisplay.map(bank => {
        const logoHtml = bank.logo
            ? `<img src="${bank.logo}" alt="${bank.name}" onerror="this.onerror=null; this.outerHTML='<div class=&quot;bank-icon-placeholder&quot;><i data-lucide=&quot;landmark&quot;></i></div>'; if(window.lucide) window.lucide.createIcons();">`
            : `<div class="bank-icon-placeholder"><i data-lucide="landmark"></i></div>`;

        const historyText = currentLanguage === "de"
            ? `${bank.transaction_total_days} TAGE VERLAUF • ${bank.bic || 'BIC UNBEKANNT'}`
            : `${bank.transaction_total_days} DAYS HISTORY • ${bank.bic || 'BIC UNKNOWN'}`;

        return `
            <div class="bank-card">
                ${logoHtml}
                <div class="bank-card-info">
                    <h4>${bank.name}</h4>
                    <p>${historyText}</p>
                </div>
            </div>
        `;
    }).join("");

    if (hasMore) {
        const headline = currentLanguage === "de"
            ? `Und ${filtered.length - displayLimit} weitere Institute...`
            : `And ${filtered.length - displayLimit} more institutions...`;
        const desc = currentLanguage === "de"
            ? `Nutzen Sie die Suche oben, um Ihre spezifische Bank in ${currentCountry} zu finden.`
            : `Use the search bar above to find your specific bank in ${currentCountry}.`;

        html += `
            <div class="explorer-info-msg">
                <i data-lucide="search-check"></i>
                <h4>${headline}</h4>
                <p>${desc}</p>
            </div>
        `;
    }

    banksGrid.innerHTML = html;

    // @ts-ignore
    if (window.lucide) window.lucide.createIcons();
}
